/* assets/calendario-grid.js — interacción expandible y FLIP del grid */
(function () {
  'use strict';

  const FLIP_DURATION = 500;
  const FLIP_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const controllers = new WeakMap();

  function getItemsPerRow(grid) {
    const columns = getComputedStyle(grid).gridTemplateColumns
      .split(' ')
      .filter(Boolean).length;
    if (columns > 0) return columns;
    if (window.innerWidth <= 820) return 2;
    if (window.innerWidth <= 1100) return 3;
    return 4;
  }

  function getCards(state) {
    return Array.from(state.grid.querySelectorAll('.event-card'));
  }

  function getCardsInOriginalOrder(state) {
    return getCards(state).sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index));
  }

  function cancelFlipAnimations(state) {
    getCards(state).forEach(card => {
      card.getAnimations().forEach(animation => {
        if (animation.id === 'event-card-flip') animation.cancel();
      });
    });
  }

  function reorderGrid(state, openedCard = null) {
    const cards = getCardsInOriginalOrder(state);
    if (!openedCard) {
      state.grid.append(...cards);
      return;
    }
    const itemsPerRow = getItemsPerRow(state.grid);
    const openIndex = Number(openedCard.dataset.index);
    const rowStartIndex = Math.floor(openIndex / itemsPerRow) * itemsPerRow;
    const cardsBefore = cards.filter(card => Number(card.dataset.index) < rowStartIndex);
    const cardsAfter = cards.filter(card => Number(card.dataset.index) >= rowStartIndex && card !== openedCard);
    state.grid.append(...cardsBefore, openedCard, ...cardsAfter);
  }

  function animateLayout(state, changeLayout) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      changeLayout();
      return;
    }
    cancelFlipAnimations(state);
    const firstRects = new Map();
    getCards(state).forEach(card => firstRects.set(card, card.getBoundingClientRect()));
    changeLayout();
    state.grid.getBoundingClientRect();

    getCards(state).forEach(card => {
      const first = firstRects.get(card);
      const last = card.getBoundingClientRect();
      if (!first || !last) return;
      const deltaX = first.left - last.left;
      const deltaY = first.top - last.top;
      const scaleX = first.width / last.width;
      const scaleY = first.height / last.height;
      if (Math.abs(deltaX) <= 0.5 && Math.abs(deltaY) <= 0.5 && Math.abs(scaleX - 1) <= 0.005 && Math.abs(scaleY - 1) <= 0.005) return;
      const animation = card.animate([
        { transform: `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`, transformOrigin: 'top left' },
        { transform: 'translate(0, 0) scale(1, 1)', transformOrigin: 'top left' }
      ], { duration: FLIP_DURATION, easing: FLIP_EASING, fill: 'both' });
      animation.id = 'event-card-flip';
      animation.addEventListener('finish', () => animation.cancel(), { once: true });
    });
  }

  function updateCardAccessibility(card, isOpen) {
    const openButton = card.querySelector('.event-card__open-button');
    if (openButton) openButton.setAttribute('aria-expanded', String(isOpen));
  }

  function focusElement(element) {
    if (element && typeof element.focus === 'function') {
      element.focus({ preventScroll: true });
    }
  }

  function openCard(state, card, opener) {
    if (card.classList.contains('is-open')) return;
    const currentOpen = state.grid.querySelector('.event-card.is-open');
    animateLayout(state, () => {
      if (currentOpen) {
        currentOpen.classList.remove('is-open');
        updateCardAccessibility(currentOpen, false);
      }
      card.classList.add('is-open');
      updateCardAccessibility(card, true);
      state.openedCard = card;
      state.opener = opener;
      reorderGrid(state, card);
    });
    requestAnimationFrame(() => focusElement(card.querySelector('.event-card__close')));
  }

  function closeCard(state, card) {
    if (!card || !card.classList.contains('is-open')) return;
    const opener = state.opener;
    animateLayout(state, () => {
      card.classList.remove('is-open');
      updateCardAccessibility(card, false);
      state.openedCard = null;
      state.opener = null;
      reorderGrid(state);
    });
    requestAnimationFrame(() => focusElement(opener));
  }

  function resetController(state) {
    cancelFlipAnimations(state);
    const openedCard = state.grid.querySelector('.event-card.is-open');
    if (openedCard) {
      openedCard.classList.remove('is-open');
      updateCardAccessibility(openedCard, false);
    }
    state.openedCard = null;
    state.opener = null;
    reorderGrid(state);
  }

  function handleGridClick(state, event) {
    const openButton = event.target.closest('.event-card__open-button');
    if (openButton && state.grid.contains(openButton)) {
      event.preventDefault();
      event.stopPropagation();
      openCard(state, openButton.closest('.event-card'), openButton);
      return;
    }

    const closeButton = event.target.closest('.event-card__close');
    if (closeButton && state.grid.contains(closeButton)) {
      event.preventDefault();
      event.stopPropagation();
      closeCard(state, closeButton.closest('.event-card'));
      return;
    }

    const favoriteButton = event.target.closest('.event-card__favorite');
    if (favoriteButton && state.grid.contains(favoriteButton)) {
      event.preventDefault();
      event.stopPropagation();
      const isFavorite = favoriteButton.classList.toggle('is-favorite');
      favoriteButton.setAttribute('aria-pressed', String(isFavorite));
      favoriteButton.setAttribute('aria-label', isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos');
    }
  }

  function mount(root) {
    if (!root) return null;
    const grid = root.querySelector('[data-cal="events-grid"]');
    if (!grid) return null;

    let state = controllers.get(root);
    if (!state) {
      state = { root, grid, openedCard: null, opener: null, previousColumns: getItemsPerRow(grid) };
      state.handleClick = event => handleGridClick(state, event);
      state.handleKeydown = event => {
        if (event.key !== 'Escape') return;
        const openedCard = state.grid.querySelector('.event-card.is-open');
        if (openedCard) closeCard(state, openedCard);
      };
      state.handleResize = () => {
        const columns = getItemsPerRow(state.grid);
        if (columns === state.previousColumns) return;
        state.previousColumns = columns;
        reorderGrid(state, state.grid.querySelector('.event-card.is-open'));
      };
      grid.addEventListener('click', state.handleClick);
      root.addEventListener('keydown', state.handleKeydown);
      window.addEventListener('resize', state.handleResize);
      controllers.set(root, state);
    } else {
      if (state.grid !== grid) {
        state.grid.removeEventListener('click', state.handleClick);
        state.grid = grid;
        state.grid.addEventListener('click', state.handleClick);
      }
      state.previousColumns = getItemsPerRow(grid);
    }
    return state;
  }

  function reset(root) {
    const state = controllers.get(root);
    if (state) resetController(state);
  }

  window.CalendarioGrid = { mount, reset };

  function initAll() {
    document.querySelectorAll('[data-cal="root"]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
